import { useEffect, useRef, useState } from "react";
import { Avatar, IconButton, CircularProgress, Box } from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import imageCompression from "browser-image-compression";
import { storage } from "../../utils/firebase/firebase";

// Cache em memória por sessão do app
// sellerId -> string | null
const avatarUrlCache = new Map();

const getInitialLetter = (name) => {
    if (!name) return "";
    return name.trim().charAt(0).toUpperCase();
};

const UserAvatar = ({ name, sellerId, seller, styleprops, typeOf = "list" }) => {
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef();

    useEffect(() => {
        const fetchAvatar = async () => {
            if (!sellerId) {
                setAvatarUrl(null);
                return;
            }

            // Se já temos cache para esse sellerId, usa direto
            if (avatarUrlCache.has(sellerId)) {
                setAvatarUrl(avatarUrlCache.get(sellerId));
                return;
            }

            try {
                const imageRef = ref(storage, `users_profile/${sellerId}.jpg`);
                const url = await getDownloadURL(imageRef);
                avatarUrlCache.set(sellerId, url);
                setAvatarUrl(url);
            } catch (error) {
                // Se for "object-not-found", é esperado (não tem foto)
                if (error?.code !== "storage/object-not-found") {
                    console.error("Erro ao buscar avatar:", error);
                }
                avatarUrlCache.set(sellerId, null);
                setAvatarUrl(null);
            }
        };

        fetchAvatar();
    }, [sellerId]);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !sellerId) return;

        setUploading(true);
        try {
            const options = {
                maxSizeMB: 0.2,
                maxWidthOrHeight: 500,
                useWebWorker: true,
            };

            const compressedFile = await imageCompression(file, options);
            const imageRef = ref(storage, `users_profile/${sellerId}.jpg`);

            await uploadBytes(imageRef, compressedFile);

            const url = await getDownloadURL(imageRef);
            avatarUrlCache.set(sellerId, url); // atualiza cache
            setAvatarUrl(url);
        } catch (error) {
            console.error("Erro ao fazer upload:", error);
        } finally {
            setUploading(false);
        }
    };

    // ====== LIST MODE ======
    if (typeOf === "list") {
        const hasAvatar = !!avatarUrl;

        return (
            <Avatar
                src={hasAvatar ? avatarUrl : undefined}
                sx={{
                    width: hasAvatar ? 48 : 24,
                    height: hasAvatar ? 48 : 24,
                    bgcolor: !seller?.firebase_disabled ? "#2e7d32" : "#c62828",
                    color: "#ffffff",
                    fontWeight: "bold",
                    fontSize: "1rem",
                    boxShadow: hasAvatar ? "0px 2px 6px rgba(0,0,0,0.2)" : "none",
                    ...styleprops,
                }}
            >
                {!hasAvatar && getInitialLetter(name)}
            </Avatar>
        );
    }

    // ====== CARD MODE (com upload) ======
    if (typeOf === "card") {
        const hasAvatar = !!avatarUrl;

        return (
            <Box
                sx={{
                    position: "relative",
                    width: styleprops?.width || 48,
                    height: styleprops?.height || 48,
                    ...styleprops,
                }}
            >
                <Avatar
                    src={!uploading && hasAvatar ? avatarUrl : undefined}
                    sx={{
                        width: "100%",
                        height: "100%",
                        bgcolor: !seller?.firebase_disabled ? "#2e7d32" : "#c62828",
                        color: "#ffffff",
                        fontWeight: "bold",
                        fontSize: "1rem",
                        boxShadow: hasAvatar ? "0px 2px 6px rgba(0,0,0,0.2)" : "none",
                        overflow: "hidden",
                        transition: "0.2s ease",
                    }}
                >
                    {uploading ? (
                        <CircularProgress size={20} />
                    ) : (
                        !hasAvatar && getInitialLetter(name)
                    )}
                </Avatar>

                <IconButton
                    component="label"
                    size="small"
                    sx={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        transform: "translate(25%, 25%)",
                        backgroundColor: "rgba(255,255,255,0.4)",
                        width: 22,
                        height: 22,
                        zIndex: 2,
                        boxShadow: "0px 2px 4px rgba(0,0,0,0.25)",
                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.7)",
                        },
                    }}
                    disabled={uploading}
                >
                    <UploadIcon fontSize="small" style={{ fontSize: 14 }} />
                    <input
                        type="file"
                        accept="image/*"
                        hidden
                        ref={inputRef}
                        onChange={handleUpload}
                    />
                </IconButton>
            </Box>
        );
    }

    // fallback seguro se vier typeOf diferente
    return (
        <Avatar
            sx={{
                width: 40,
                height: 40,
                bgcolor: !seller?.firebase_disabled ? "#2e7d32" : "#c62828",
                color: "#ffffff",
                fontWeight: "bold",
            }}
        >
            {getInitialLetter(name)}
        </Avatar>
    );
};

export default UserAvatar;
